import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {APIError} from 'data/errors';
import {makePostOperationalCostPassword} from 'main/factories/usecases';
import {EmergencyTelehealthRoomUnavailableError} from 'presentation/components/EmergencyTelehealthUnavailableRoomModal/error';
import {
	AuthorizationsEnum,
	FavoritesEnum,
	FeaturesEnum,
	OperationStateType,
	OperationStateTypesEnum,
} from 'presentation/constants';

import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {useBookingConfirmationDrawer} from 'presentation/pages/private/booking/hooks/useBookingConfirmationDrawer';
import {ServicesTypesEnum} from 'presentation/pages/private/services/types';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useOperationalCostErrorMessageStore, useOperationalCostStore} from './store';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useFeatureFlag} from '../useFeatureFlag';
import {useHealthEmergencyLinkFactory} from '../useHealthEmergencyLinkFactory';

export function useOperationalCostPassword() {
	const makeHealthEmergencyLink = useHealthEmergencyLinkFactory();
	const bookingData = useBookingStore(state => state.bookingData);
	const navigate = useNavigate();
	const {setSelectedServiceType, handleOpenBookingConfirmationDrawer} =
		useBookingConfirmationDrawer();
	const {sidebar} = useAccountDataStore(state => state.accountData);
	const isOperationalCostAuthorized = useOperationalCostStore(
		state => state.isOperationalCostAuthorized,
	);
	const setIsOperationalCostAuthorized = useOperationalCostStore(
		state => state.setIsOperationalCostAuthorized,
	);

	const passwordErrorMessage = useOperationalCostErrorMessageStore(state => state.errorMessage);
	const setPasswordErrorMessage = useOperationalCostErrorMessageStore(
		state => state.setErrorMessage,
	);

	const [operationStatePass, setOperationStatePass] = useState<OperationStateType>(
		OperationStateTypesEnum.NONE,
	);
	const [operationStateUrl, setOperationStateUrl] = useState<OperationStateType>(
		OperationStateTypesEnum.NONE,
	);

	const [openConfirmationDrawer, setOpenConfirmationDrawer] = useState(false);

	const handleOpenConfirmationDrawer = () => setOpenConfirmationDrawer(true);
	const handleCloseConfirmationDrawer = () => setOpenConfirmationDrawer(false);

	const {isEnabled: isSchedulingByMedicalGuideEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULING_BY_MEDICAL_GUIDE,
	);

	const IS_SUBMITTING_PASS = operationStatePass === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR_PASS = operationStatePass === OperationStateTypesEnum.ERROR;

	const IS_SUBMITTING_GET_URL = operationStateUrl === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR_GET_URL = operationStateUrl === OperationStateTypesEnum.ERROR;

	async function handleVerifyOperationalCostPassword(
		operationalPass: string,
		type: ServicesTypesEnum,
	) {
		if (!sidebar.ctOperacional) {
			return;
		}
		try {
			setOperationStatePass(OperationStateTypesEnum.SUBMITTING);
			await makePostOperationalCostPassword().post({
				cdEmpresa: sidebar.ctOperacional.cdEmpresa,
				nuSenha: operationalPass,
			});

			setIsOperationalCostAuthorized(true);
			setOperationStatePass(OperationStateTypesEnum.DONE);

			if (type === ServicesTypesEnum.HEALTH) {
				setSelectedServiceType(BookingTypeEnum.HEALTH);
				if (bookingData?.isFavoriteDoctorsFlow) {
					navigate(FavoritesEnum.FAVORITE_DOCTORS);
					return;
				}
				if (isSchedulingByMedicalGuideEnabled) {
					handleOpenConfirmationDrawer();
					return;
				}
				handleOpenBookingConfirmationDrawer();
				return;
			}

			if (type === ServicesTypesEnum.EXAMS) {
				setSelectedServiceType(BookingTypeEnum.EXAM);
				handleOpenBookingConfirmationDrawer();
				return;
			}

			if (type === ServicesTypesEnum.ODONTO) {
				setSelectedServiceType(BookingTypeEnum.ODONTO);
				handleOpenBookingConfirmationDrawer();
				return;
			}

			if (type === ServicesTypesEnum.TELEHEALTH) {
				setSelectedServiceType(BookingTypeEnum.TELEHEALTH);
				handleOpenBookingConfirmationDrawer();
				return;
			}

			if (type === ServicesTypesEnum.EMERGENCY_TELEHEALTH) {
				setOperationStateUrl(OperationStateTypesEnum.SUBMITTING);
				await makeHealthEmergencyLink()
					.get()
					.then(res => {
						window.open(res);
						navigate(-1);
						setOperationStateUrl(OperationStateTypesEnum.DONE);
					})
					.catch(error => {
						if (error instanceof EmergencyTelehealthRoomUnavailableError) {
							setOperationStateUrl(OperationStateTypesEnum.NONE);
							return;
						}

						setOperationStateUrl(OperationStateTypesEnum.ERROR);
					});

				return;
			}

			if (type === ServicesTypesEnum.AUTHORIZATIONS) {
				setOperationStateUrl(OperationStateTypesEnum.SUBMITTING);
				navigate(AuthorizationsEnum.AUTHORIZATIONS);
			}
		} catch (error) {
			setOperationStatePass(OperationStateTypesEnum.ERROR);
			if (error instanceof APIError) {
				setPasswordErrorMessage(error.message);
			}
		}
	}

	return {
		handleVerifyOperationalCostPassword,
		IS_SUBMITTING_PASS,
		IS_ERROR_PASS,
		IS_SUBMITTING_GET_URL,
		IS_ERROR_GET_URL,
		isOperationalCostAuthorized,
		passwordErrorMessage,
		setOperationStatePass,
		setOperationStateUrl,
		operationStateUrl,
		openConfirmationDrawer,
		handleOpenConfirmationDrawer,
		handleCloseConfirmationDrawer,
	};
}
